import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import { portalBrandingApi } from '../../services/portalApi';
import { Ticket, LogOut, User, Menu, X, Monitor, FileText, FileCheck, FolderKanban, BookOpen, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Suspense, useState, useEffect } from 'react';
import { RouteFallback } from '../shared/RouteFallback';

export default function PortalLayout() {
  const { user, logout, hasPermission, isSudo } = usePortalAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    portalBrandingApi.mine().then(({ data }) => setBranding(data)).catch(() => {});
  }, [user?.clientId]);

  const handleLogout = async () => {
    await logout();
    navigate('/portal/login');
  };

  const navItems = [
    { label: 'Tickets', path: '/portal/tickets', icon: Ticket, show: hasPermission('tickets', 'view') },
    { label: 'Assets', path: '/portal/assets', icon: Monitor, show: hasPermission('assets', 'view') },
    { label: 'Invoices', path: '/portal/invoices', icon: FileText, show: hasPermission('invoices', 'view') && branding?.showInvoices !== false },
    { label: 'Contracts', path: '/portal/contracts', icon: FileCheck, show: hasPermission('contracts', 'view') },
    { label: 'Projects', path: '/portal/projects', icon: FolderKanban, show: hasPermission('projects', 'view') },
    { label: 'KB', path: '/portal/kb', icon: BookOpen, show: hasPermission('knowledgeBase', 'view') },
    { label: 'Team', path: '/portal/users', icon: Users, show: hasPermission('manageUsers') },
  ];

  const isActive = (path) => location.pathname.startsWith(path);

  return (
    <div className="min-h-screen bg-[#f8f9fb] dark:bg-[#0a0a0b]" data-testid="portal-layout">
      {/* Sudo Banner */}
      {isSudo && (
        <div className="bg-amber-500 text-black text-center py-2 px-4 text-sm font-medium" data-testid="sudo-banner">
          You are viewing as {user?.name} — {user?.clientName}
          <Button variant="outline" size="sm" className="ml-4 h-6 text-xs" data-testid="sudo-exit-btn" onClick={() => {
            localStorage.removeItem('portalSudoToken');
            localStorage.removeItem('portalAccessToken');
            localStorage.removeItem('portalUser');
            window.close();
            // If this was not opened by a script the tab stays put — take them
            // somewhere sensible rather than leaving a dead read-only screen.
            navigate('/portal/login');
          }}>
            Exit
          </Button>
        </div>
      )}

      {/* Top Navigation Bar */}
      <header className="bg-white dark:bg-[#141417] border-b border-gray-200 dark:border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            {/* Logo / Brand */}
            <div className="flex items-center gap-6">
              <Link to="/portal" className="flex items-center gap-2" data-testid="portal-brand">
                {branding?.logoUrl ? (
                  <img src={branding.logoUrl} alt="" className="h-8 object-contain" />
                ) : (
                  <div className="w-8 h-8 rounded-lg bg-black dark:bg-white flex items-center justify-center">
                    <span className="text-white dark:text-black font-bold text-sm">
                      {(branding?.companyName || user?.clientName || 'C').trim().charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <span className="font-semibold text-sm text-gray-900 dark:text-white hidden sm:block">
                  {branding?.companyName || user?.clientName || 'Client Portal'}
                </span>
              </Link>

              {/* Desktop Nav */}
              <nav className="hidden md:flex items-center gap-1">
                {navItems.filter(i => i.show).map(item => (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`portal-nav-${item.label.toLowerCase()}`}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
                      isActive(item.path)
                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white font-medium'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800/50'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-3">
              <Link to="/portal/profile" className="hidden sm:flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white" data-testid="portal-profile-link">
                <User className="w-4 h-4" />
                <span>{user?.name}</span>
              </Link>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-gray-500 hover:text-gray-900 dark:hover:text-white"
                data-testid="portal-logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>

              {/* Mobile menu toggle */}
              <Button
                variant="ghost"
                size="sm"
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 dark:border-gray-800 px-4 py-2 space-y-1">
            {navItems.filter(i => i.show).map(item => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm ${
                  isActive(item.path)
                    ? 'bg-gray-100 dark:bg-gray-800 font-medium'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            ))}
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <Suspense fallback={<RouteFallback />}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
}
